import type { ProtectionState } from '../types';

/**
 * Manages dynamic element observation using MutationObserver
 */
export class ElementObserver {
    private observer: MutationObserver | null = null;
    private selectors: string[] = [];
    private protectedElements: Map<Element, ProtectionState> = new Map();
    private onElementAdded: ((element: Element) => void) | null = null;
    private onElementRemoved: ((element: Element, state: ProtectionState) => void) | null = null;

    /**
     * Starts observing the DOM for elements matching the given selectors
     */
    start(
        selectors: string[],
        onAdd: (element: Element) => void,
        onRemove: (element: Element, state: ProtectionState) => void
    ): void {
        this.selectors = selectors;
        this.onElementAdded = onAdd;
        this.onElementRemoved = onRemove;

        // Create observer but do not observe yet – run initial scan first so our own DOM
        // changes (containers, moved nodes) do not trigger handleMutations and cause a loop
        this.observer = new MutationObserver(this.handleMutations.bind(this));
        this.scanAndProtect(document.body);
        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    /**
     * Stops observing and cleans up all protected elements
     */
    stop(): void {
        this.observer?.disconnect();
        this.observer = null;

        // Clean up all protected elements
        if (this.onElementRemoved) {
            this.protectedElements.forEach((state, element) => {
                this.onElementRemoved!(element, state);
            });
        }

        this.protectedElements.clear();

        this.selectors = [];
        this.onElementAdded = null;
        this.onElementRemoved = null;
    }

    /**
     * Checks if an element matches any of the target selectors
     */
    matchesAnySelector(element: Element): boolean {
        return this.selectors.some((selector) => {
            try {
                return element.matches(selector);
            } catch {
                return false;
            }
        });
    }

    /**
     * Gets the combined selector string for querySelectorAll
     */
    getCombinedSelector(): string {
        return this.selectors.join(',');
    }

    /**
     * Registers an element as protected
     */
    registerProtection(element: Element, state: ProtectionState): void {
        this.protectedElements.set(element, state);
    }

    /**
     * Gets the protection state for an element
     */
    getProtection(element: Element): ProtectionState | undefined {
        return this.protectedElements.get(element);
    }

    /**
     * Checks if an element is already protected
     */
    isProtected(element: Element): boolean {
        return this.protectedElements.has(element);
    }

    /**
     * Removes protection registration for an element
     */
    unregisterProtection(element: Element): void {
        this.protectedElements.delete(element);
    }

    /**
     * Returns the number of protected elements
     */
    getProtectedCount(): number {
        return this.protectedElements.size;
    }

    /** Class used by our blur overlay container; elements inside must not be re-protected */
    private static readonly CONTAINER_CLASS = 'copydeter-container';

    /** Returns true for elements created by copydeter (containers or sibling clones) */
    private static isOwnElement(el: Element): boolean {
        return (
            !!el.closest(`.${ElementObserver.CONTAINER_CLASS}`) ||
            el.hasAttribute('data-copydeter-clone')
        );
    }

    /**
     * Scans a subtree for matching elements and applies protection
     */
    private scanAndProtect(root: Element): void {
        if (!this.onElementAdded || this.selectors.length === 0) {
            return;
        }

        if (ElementObserver.isOwnElement(root)) {
            return;
        }

        // Check if root itself matches
        if (this.matchesAnySelector(root) && !this.isProtected(root)) {
            this.onElementAdded(root);
        }

        // Check all descendants
        try {
            const matches = root.querySelectorAll(this.getCombinedSelector());
            const toProtect = Array.from(matches).filter(
                (el) => !this.isProtected(el) && !ElementObserver.isOwnElement(el)
            );

            for (const element of toProtect) {
                this.onElementAdded(element);
            }
        } catch {
            // Invalid selector, skip
        }
    }

    /**
     * Handles DOM mutations.
     * Disconnects for the duration of processing so our own DOM changes (containers, moved
     * nodes) do not trigger another callback and cause a loop.
     */
    private handleMutations(mutations: MutationRecord[]): void {
        if (!this.observer) {
            return;
        }

        this.observer.disconnect();

        try {
            mutations.forEach((mutation) => {
                // Handle added nodes
                Array.from(mutation.addedNodes).forEach((node) => {
                    if (node instanceof Element) {
                        this.scanAndProtect(node);
                    }
                });

                // Handle removed nodes
                Array.from(mutation.removedNodes).forEach((node) => {
                    if (node instanceof Element) {
                        this.cleanupRemovedElement(node);
                    }
                });
            });
        } finally {
            this.observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    /**
     * Cleans up a removed element and its descendants
     */
    private cleanupRemovedElement(element: Element): void {
        // Check if the element itself was protected
        const state = this.protectedElements.get(element);

        if (state && this.onElementRemoved) {
            this.onElementRemoved(element, state);
            this.protectedElements.delete(element);
        }

        // Check descendants
        try {
            const matches = element.querySelectorAll(this.getCombinedSelector());

            Array.from(matches).forEach((descendant) => {
                const descState = this.protectedElements.get(descendant);

                if (descState && this.onElementRemoved) {
                    this.onElementRemoved!(descendant, descState);
                    this.protectedElements.delete(descendant);
                }
            });
        } catch {
            // Invalid selector, skip
        }
    }
}
